import { useFetchAllRows, UseFetchAllRowsResult } from '@/hooks/useFetchAllRows'
import { getAllReturnInbounds } from './apis'
import { MAX_FETCH_SIZE, ReturnInboundItem, ReturnInboundSearchParams } from './types'

// 맨 앞은 메뉴코드 — 탭을 닫으면 MenuOnSessionWrap.remove 가 이 코드가 든 키를 지운다(egr06 선례).
const PATH = ['rgr13', 'return', 'returnInbound'] as const

export const returnInboundQueryKeys = {
  all: PATH,
  list: (params: ReturnInboundSearchParams | null) => [...PATH, 'LIST', params] as const,
}

type ReturnInboundListResult = Omit<UseFetchAllRowsResult<ReturnInboundItem>, 'rows'> & { items: ReturnInboundItem[] }

const getKey = (row: ReturnInboundItem) => row.id

/**
 * 반품입고 목록 — 조건에 걸린 전건을 BE size 상한(`MAX_FETCH_SIZE`) 구간으로 나눠 받는다(LOGISPRD-15269).
 *
 * 표의 페이징·정렬·필터·목록검색이 전부 이 결과를 대상으로 돈다(핸드오프 데이터 계약이
 * 페이징 인자 없는 `fetchTable()` 전건 조회다). 서버 페이징으로 나누면 "이 페이지 안에서만"
 * 정렬·검색되어 1.0 과 다른 답이 나온다.
 *
 * `params` 가 없으면 조회하지 않는다 — 화주·입고예정일자가 필수라 조회 버튼을 눌러야 조건이 선다.
 * 실패 안내는 useFetchAllRows 의 전역 알럿(서버 meta.message) 하나다 — 화면이 따로 띄우지 않는다.
 */
export const useGetAllReturnInbounds = (params: ReturnInboundSearchParams | null): ReturnInboundListResult => {
  const { rows, ...rest } = useFetchAllRows<ReturnInboundItem>({
    queryKey: returnInboundQueryKeys.list(params),
    fetchPage: async (page, size) => {
      const { data } = await getAllReturnInbounds({ ...(params as ReturnInboundSearchParams), page, size: size ?? MAX_FETCH_SIZE })
      // pageInfo 가 빠진 응답을 0건으로 메우지 않는다 — 0 이면 첫 구간에서 조용히 멈춘다. 없으면 여기서 던져 실패 알럿으로 드러낸다.
      return { items: data.items, totalCount: data.pageInfo.totalCount }
    },
    chunkSize: MAX_FETCH_SIZE,
    getKey,
    enabled: params !== null,
  })
  return { items: rows, ...rest }
}
